import React from "react";
import { Deal, Tone } from "../model/types";
import { stageKindLabel } from "../model/lifecycle";
import {
    getComplianceScore,
    getLeversScore,
    getPolicyForDeal,
} from "../model/api";
import { useTailSpend } from "../TailSpendContext";
import { StatusTag } from "./StatusTag";
import classes from "./DealHeaderCard.module.less";

const scoreTone = (achieved: number, total: number): Tone => {
    if (total === 0) return "slate";
    if (achieved === total) return "green";
    if (achieved === 0) return "red";
    return "amber";
};

interface Props {
    deal: Deal;
    actions?: React.ReactNode;
}

const initials = (name: string) =>
    name
        .split(/\s+/)
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

const findStat = (deal: Deal, ...needles: string[]): string | undefined => {
    for (const n of needles) {
        const hit = deal.statValuePairs.find(([k]) => k.toLowerCase() === n.toLowerCase());
        if (hit) return hit[1];
    }
    return undefined;
};

const opportunityOf = (deal: Deal): string => {
    if (deal.opportunityRange) {
        const { low, high } = deal.opportunityRange;
        return `$${low.amount.toLocaleString()} – $${high.amount.toLocaleString()}`;
    }
    return findStat(deal, "Opportunity") ?? "—";
};

const routingOf = (deal: Deal): string => {
    switch (deal.state.kind) {
        case "Triage":
            return "Pending triage";
        case "Negotiating":
        case "AwaitingConfirmation":
            return "Autonomous";
        case "Executing":
            return deal.state.routing === "buyer_esign" ? "Buyer e-sign" : "Auto-execute";
        case "ClosedWon":
            return findStat(deal, "Routing") ?? "Auto-execute";
        case "Escalated":
            return "Paused · needs you";
        case "Held":
            return "Held · above cap";
        case "Walked":
            return "Walked";
        case "Reassigned":
            return "Strategic team";
    }
};

export const DealHeaderCard: React.FC<Props> = ({ deal, actions }) => {
    const { workspace } = useTailSpend();
    const policy = getPolicyForDeal(workspace, deal);
    const levers = getLeversScore(deal);
    const compliance = getComplianceScore(deal, policy);

    const leversNode =
        levers.total === 0 ? (
            <span className={classes.scoreMuted}>—</span>
        ) : (
            <StatusTag tone={scoreTone(levers.inEnvelope, levers.total)}>
                {levers.inEnvelope}/{levers.total}
            </StatusTag>
        );

    const complianceNode =
        compliance.total === 0 ? (
            <span className={classes.scoreMuted}>—</span>
        ) : (
            <StatusTag tone={scoreTone(compliance.confirmed, compliance.total)}>
                {compliance.confirmed}/{compliance.total}
            </StatusTag>
        );

    const fields: Array<[string, React.ReactNode]> = [
        ["Levers", leversNode],
        ["Compliance", complianceNode],
        ["Opportunity", opportunityOf(deal)],
        ["Routing", routingOf(deal)],
    ];

    return (
        <div className={classes.card}>
            <div className={classes.logo}>{initials(deal.supplierName)}</div>
            <div className={classes.content}>
                <div className={classes.primaryField}>
                    <div className={classes.primaryName}>{deal.supplierName}</div>
                    <div className={classes.primarySub}>
                        {deal.categoryName} ·{" "}
                        <span style={{ fontFamily: "monospace" }}>
                            ${deal.annualValue.amount.toLocaleString()}
                        </span>{" "}
                        annual · {deal.supplierEmail}
                    </div>
                </div>

                <div className={`${classes.field} ${classes.statusField}`}>
                    <div className={classes.fieldLabel}>Status</div>
                    <div className={classes.fieldValue}>
                        <StatusTag tone={deal.statusChip.tone}>
                            {deal.statusChip.label || stageKindLabel(deal.state)}
                        </StatusTag>
                    </div>
                </div>

                {fields.map(([label, value]) => (
                    <div key={label} className={classes.field}>
                        <div className={classes.fieldLabel}>{label}</div>
                        <div className={classes.fieldValue}>{value}</div>
                    </div>
                ))}

                {actions && <div className={classes.actions}>{actions}</div>}
            </div>
        </div>
    );
};
